import { useState } from "react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

// Canonical action -> backend action codes granted when ticked.
const ACTION_MATRIX = [
  { key: "view", label: "View", codes: ["view"] },
  { key: "create", label: "Create", codes: ["create"] },
  { key: "edit", label: "Edit", codes: ["edit"] },
  { key: "delete", label: "Delete", codes: ["delete", "archive"] },
  { key: "export", label: "Export", codes: ["export"] },
  { key: "approve", label: "Approve / Finalize", codes: ["approve", "finalize", "manage"] },
];

// Society-Admin friendly label -> backend module key enforced by society_permission.
const MODULE_MATRIX = [
  { key: "member_master", label: "Members" },
  { key: "units", label: "Units" },
  { key: "ownership", label: "Ownership" },
  { key: "finance_billing", label: "Billing" },
  { key: "finance_collections", label: "Receipts" },
  { key: "accounting_settings", label: "Financial Foundation" },
  { key: "finance_reports", label: "Member Ledger & Reports" },
  { key: "documents", label: "Documents" },
  { key: "governance", label: "Governance" },
  { key: "member_import", label: "Old Data Import" },
  { key: "settings", label: "Society Access" },
];

const MATRIX_CODES = new Set(ACTION_MATRIX.flatMap((action) => action.codes));

function permissionsToEditor(permissions = {}) {
  const grid = {};
  const preserved = {};
  for (const [module, actions] of Object.entries(permissions)) {
    const matrixModule = MODULE_MATRIX.some((item) => item.key === module);
    if (!matrixModule) {
      preserved[module] = actions;
      continue;
    }
    grid[module] = {};
    for (const action of ACTION_MATRIX) {
      grid[module][action.key] = action.codes.some((code) => actions.includes(code));
    }
    const extra = actions.filter((action) => !MATRIX_CODES.has(action));
    if (extra.length) preserved[module] = extra;
  }
  return { grid, preserved };
}

export default function SocietyAccessPanel({ memberships, onSaved, roles }) {
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [roleName, setRoleName] = useState("");
  const [roleDesc, setRoleDesc] = useState("");
  const [grid, setGrid] = useState({}); // { moduleKey: { actionKey: true } }
  const [preservedPermissions, setPreservedPermissions] = useState({});
  const [editingRole, setEditingRole] = useState(null);
  const [saving, setSaving] = useState(false);

  const toggle = (mod, act) =>
    setGrid((g) => ({ ...g, [mod]: { ...(g[mod] || {}), [act]: !(g[mod] || {})[act] } }));

  const toggleRow = (mod, value) =>
    setGrid((g) => ({ ...g, [mod]: Object.fromEntries(ACTION_MATRIX.map((a) => [a.key, value])) }));

  const assign = async () => {
    if (!email || !roleId) {
      toast.error("Existing user email and society role are required");
      return;
    }
    try {
      await api.post("/society-setup/memberships", { email, society_role_id: roleId });
      setEmail("");
      toast.success("Society access assigned");
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not assign society access");
    }
  };

  const buildPermissions = () => {
    const permissions = { ...preservedPermissions };
    for (const mod of MODULE_MATRIX) {
      const ticked = grid[mod.key] || {};
      const codes = new Set(preservedPermissions[mod.key] || []);
      for (const act of ACTION_MATRIX) {
        if (ticked[act.key]) act.codes.forEach((code) => codes.add(code));
      }
      if (codes.size) permissions[mod.key] = [...codes].sort();
      else delete permissions[mod.key];
    }
    return permissions;
  };

  const resetRoleEditor = () => {
    setRoleName("");
    setRoleDesc("");
    setGrid({});
    setPreservedPermissions({});
    setEditingRole(null);
  };

  const saveRole = async () => {
    if (!roleName.trim()) {
      toast.error("Role name is required");
      return;
    }
    const permissions = buildPermissions();
    if (!Object.keys(permissions).length) {
      toast.error("Tick at least one permission for the role");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: roleName.trim(),
        description: roleDesc.trim() || "Custom society role",
        permissions,
      };
      if (editingRole) {
        await api.patch(`/society-setup/roles/${editingRole.society_role_id}`, payload);
        toast.success("Society role updated");
      } else {
        await api.post("/society-setup/roles", payload);
        toast.success("Society role created");
      }
      resetRoleEditor();
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create society role");
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (role) => {
    const editor = permissionsToEditor(role.permissions || {});
    setEditingRole(role);
    setRoleName(role.name || "");
    setRoleDesc(role.description || "");
    setGrid(editor.grid);
    setPreservedPermissions(editor.preserved);
  };

  const setRoleStatus = async (role, status) => {
    try {
      await api.post(`/society-setup/roles/${role.society_role_id}/status`, { status });
      toast.success(status === "ACTIVE" ? "Society role reactivated" : "Society role deactivated");
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update role status");
    }
  };

  return (
    <section className="mt-5 space-y-6" data-testid="society-access-panel">
      <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="font-display text-base font-bold text-[#0F2B63]">
          {editingRole ? `Edit ${editingRole.name}` : "Create a role"}
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Tick exactly what this role can do. Example — a "Society CA" with only View (and Export on reports).
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Input data-testid="role-name" placeholder="Role name (e.g. Society CA)" value={roleName} onChange={(e) => setRoleName(e.target.value)} />
          <Input data-testid="role-desc" placeholder="Description (optional)" value={roleDesc} onChange={(e) => setRoleDesc(e.target.value)} />
        </div>
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-100">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#0F2B63] text-xs uppercase tracking-wide text-white">
              <tr>
                <th className="px-3 py-2 font-semibold">Module</th>
                {ACTION_MATRIX.map((a) => (<th key={a.key} className="px-3 py-2 text-center font-semibold">{a.label}</th>))}
                <th className="px-3 py-2 text-center font-semibold">All</th>
              </tr>
            </thead>
            <tbody>
              {MODULE_MATRIX.map((mod, i) => {
                const row = grid[mod.key] || {};
                const allOn = ACTION_MATRIX.every((a) => row[a.key]);
                return (
                  <tr key={mod.key} className={i % 2 ? "bg-slate-50/60" : "bg-white"}>
                    <td className="px-3 py-2 font-medium text-[#0F2B63]">{mod.label}</td>
                    {ACTION_MATRIX.map((a) => (
                      <td key={a.key} className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 cursor-pointer accent-[#2563EB]"
                          checked={Boolean(row[a.key])}
                          onChange={() => toggle(mod.key, a.key)}
                          data-testid={`perm-${mod.key}-${a.key}`}
                        />
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center">
                      <input type="checkbox" className="h-4 w-4 cursor-pointer accent-emerald-600" checked={allOn} onChange={() => toggleRow(mod.key, !allOn)} data-testid={`perm-${mod.key}-all`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button className="" onClick={saveRole} disabled={saving} data-testid="society-access-save-role">
            {saving ? "Saving…" : editingRole ? "Save role" : "Create role"}
          </Button>
          {editingRole && (
            <Button data-testid="society-access-cancel-edit-role" onClick={resetRoleEditor} variant="outline">
              Cancel edit
            </Button>
          )}
        </div>
      </article>

      <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="font-display text-base font-bold text-[#0F2B63]">Assign access</h2>
        <p className="mt-1 text-sm text-slate-500">Give an existing account a role within this society only.</p>
        <div className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_auto]">
          <Input data-testid="society-access-email" onChange={(e) => setEmail(e.target.value)} placeholder="Existing user email" value={email} />
          <select className="h-10 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="society-access-role-select" onChange={(e) => setRoleId(e.target.value)} value={roleId}>
            <option value="">Choose society role</option>
            {roles.map((role) => (<option key={role.society_role_id} value={role.society_role_id}>{role.name}</option>))}
          </select>
          <Button data-testid="society-access-assign" onClick={assign}>Assign</Button>
        </div>
      </article>

      <article className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="society-roles-list">
        <table className="min-w-[640px] w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="p-3 text-left">Role</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Permissions</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr className="border-t align-top" key={role.society_role_id}>
                <td className="p-3 font-medium text-[#0F2B63]">{role.name}</td>
                <td className="p-3">
                  <span data-testid={`society-role-status-${role.society_role_id}`}>
                    {role.status || "ACTIVE"}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(role.permissions || {}).map(([mod, acts]) => (
                      <span key={mod} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{mod}: {acts.join(", ")}</span>
                    ))}
                    {!Object.keys(role.permissions || {}).length && <span className="text-xs text-slate-400">No permissions</span>}
                  </div>
                </td>
                <td className="p-3 text-right">
                  {role.is_system ? (
                    <span className="text-xs text-slate-400">System role</span>
                  ) : (
                    <div className="flex justify-end gap-2">
                      <Button
                        data-testid={`society-role-edit-${role.society_role_id}`}
                        onClick={() => startEditing(role)}
                        size="sm"
                        variant="outline"
                      >
                        Edit
                      </Button>
                      <Button
                        data-testid={`society-role-status-action-${role.society_role_id}`}
                        onClick={() => setRoleStatus(role, role.status === "ACTIVE" ? "INACTIVE" : "ACTIVE")}
                        size="sm"
                        variant={role.status === "ACTIVE" ? "destructive" : "outline"}
                      >
                        {role.status === "ACTIVE" ? "Deactivate" : "Reactivate"}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </article>

      <article className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-[640px] w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="p-3 text-left">User</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Role ID</th>
              <th className="p-3 text-left">Primary</th>
            </tr>
          </thead>
          <tbody>
            {memberships.map((membership) => (
              <tr className="border-t" key={membership.membership_id}>
                <td className="p-3">{membership.name || "—"}</td>
                <td className="p-3">{membership.email || "—"}</td>
                <td className="p-3 font-mono text-xs">{membership.society_role_id}</td>
                <td className="p-3">{membership.is_primary ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </article>
    </section>
  );
}